import {
  FormGroup,
  Card,
  HStack,
  Note,
  NoteActions,
  NoteBody,
  NoteButton,
  Spacer,
  Text,
  VStack,
  View,
  IconButton,
  AddIcon,
  ImageIcon,
  PaymentIcon,
  ElementFormBlockIcon,
  ElementAnimationIcon,
  toast,
} from '@packages/systems/spring';
// eslint-disable-next-line webflow/module-top-level-imports
import {
  Textarea,
  TextareaContainer,
  TextareaInput,
  TextareaTags,
  TextareaActions,
  HiddenFileInput,
  useTextarea,
  useFileUpload,
  type TagData,
} from '@packages/systems/ai/agent/frontend/chat';
// Deep import (not the agent/frontend barrel) to match the file's deep-import
// discipline: pulls only the picker, not the agent's heavier exports.
// eslint-disable-next-line webflow/module-top-level-imports
import {AddContentPopover} from '@packages/systems/ai/agent/frontend/messages/components/add-content-popover';
// eslint-disable-next-line webflow/module-top-level-imports
import {useCmsCollectionItemCount} from '@packages/systems/ai/agent/frontend/messages/components/useCmsCollectionItemCount';
// Deep imports (not the assistant-next barrel) so we only pull the secret
// detection hook + note, not AssistantChat and its ESM-only deps.
// eslint-disable-next-line webflow/module-top-level-imports
import {
  useSecretDetection,
  SECRET_BLOCK_REASON,
} from '@packages/systems/ai/assistant-next/frontend/hooks/useSecretDetection';
// eslint-disable-next-line webflow/module-top-level-imports
import {SecretDetectedNote} from '@packages/systems/ai/assistant-next/frontend/components/AssistantChat/SecretDetectedNote';
// Deep imports (not the assistant-next barrel) so we only pull the paste
// helpers + edit modal, not AssistantChat and its ESM-only deps.
// eslint-disable-next-line webflow/module-top-level-imports
import {
  evaluateLargePaste,
  largePasteRejectMessage,
  isPasteTagType,
  isPasteTagMetadata,
  countLines,
} from '@packages/systems/ai/assistant-next/frontend/utils/clipboard';
// eslint-disable-next-line webflow/module-top-level-imports
import {EditPasteModal} from '@packages/systems/ai/assistant-next/frontend/components/AssistantChat/EditPasteModal';
import type {PasteAttachment} from '@packages/domains/ai-assistant/data-types';
import {generateGuid} from '@packages/utilities/guid';
import type {ImageUploadResult} from '@packages/utilities/image-upload/shared';
import {emotionStyled} from '@packages/utilities/styled';
import {ComponentsCode64Icon} from '@packages/systems/spring-branded';
import {useTranslation} from '@packages/systems/designer-translations';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {selectComponentCanvasComponentId} from '@packages/systems/component-canvas/designer';
// Deep import (not the assistant-next barrel) so we only pull the CMS picker
// binding hook, not AssistantChat and its ESM-only deps.
// eslint-disable-next-line webflow/module-top-level-imports
import {useCmsPickerBinding} from '@packages/systems/ai/assistant-next/frontend/hooks/useCmsPickerBinding';
// Deep import for the same reason: keep this composer independent of the
// full AssistantChat module graph.
// eslint-disable-next-line webflow/module-top-level-imports
import {
  CmsPickerOnboardingPopover,
  useCmsPickerOnboardingPopover,
} from '@packages/systems/ai/assistant-next/frontend/components/CmsPickerOnboarding';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {warmCodeComponentStyleProfile} from './clients/fetchCodeComponentStyleProfile';
import {CodeComponentCreatorTemplates} from './CodeComponentCreatorTemplates';
import {
  selectCreatorTemplateBranding,
  useCodeComponentCreated,
  useCodeComponentSelector,
} from './flux/CodeLibrariesSlice';
import {
  selectIsLegacyGuestAiBlocked,
  selectSiteId,
  selectWorkspaceId,
} from '@packages/systems/designer/site-data';
import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';
import {trackEvent} from '@packages/utilities/analytics';
import {ViewportLayer} from '@packages/systems/ui/overlay';
import React from 'react';

const selectUserId = (state: DesignerStores): string =>
  state.SiteDataStore.userId;

import {
  useCreditBalance,
  CreditUsagePopover,
  CreditLimitReached,
  LegacyGuestAiBlock,
} from '@packages/systems/ai/agent/frontend/credit-usage';

interface Example {
  title: string;
  Icon: typeof AddIcon;
  prompt: string;
}

const EXAMPLES: Example[] = [
  {
    title: 'Pricing section',
    Icon: PaymentIcon,
    prompt:
      'Create a three-tiered pricing section for my basic, premium, and enterprise tiers. Add a web traffic slider above them that adjusts the price in each section.',
  },
  {
    title: 'Signup form',
    Icon: ElementFormBlockIcon,
    prompt:
      'Create a three-step signup form that will submit to my public endpoint. Include a progress bar that lets users know how much of the form they have completed.',
  },
  {
    title: 'Animated background',
    Icon: ElementAnimationIcon,
    prompt:
      'Create an animated and generative metaball hero section. Add props for the title, softness, and colors.',
  },
];

const ClickableCard = emotionStyled(Card)({
  border: '1px solid $border1',
  width: 'auto',
  ':hover': {
    background: '$actionSecondaryBackgroundHover',
  },
  ':focus-visible': {
    outline: '1px solid $blueBorder',
    outlineOffset: '1px',
  },
});

const DISABLED_STYLE = {opacity: 0.5, pointerEvents: 'none'} as const;

const selectShowFileUploadDropzone = (state: DesignerStores) =>
  state.AssetManagerUIStore?.dropzones?.rightSidebar || false;

const ExampleCard = ({
  example,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
}: {
  example: Example;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
}) => {
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <ClickableCard
      elevation={0}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      role="button"
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <HStack spacing={2} align="center">
        <example.Icon size={16} colorVariant="secondary" />
        <Text font="heading" colorVariant="primary">
          {example.title}
        </Text>
      </HStack>
    </ClickableCard>
  );
};

interface Props {
  onSubmit: (
    prompt: string,
    images?: ImageUploadResult[],
    fromSuggestion?: boolean,
    pasteAttachments?: PasteAttachment[]
  ) => void;
  siteShortName: string;
  showUpgradeBanner?: boolean;
  upgradeUrl?: string;
  upgradeCtaLabel?: string;
  upgradeBody?: React.ReactNode;
}

export const CodeComponentCreator = ({
  onSubmit,
  upgradeUrl,
  showUpgradeBanner,
  upgradeCtaLabel,
  upgradeBody,
  siteShortName,
}: Props) => {
  const DEFAULT_PLACEHOLDER =
    'What do you want to make? Start typing or attach a reference.';
  const {
    isAiCreditsEnabled,
    creditBalance,
    limited,
    canExceedLimit,
    softLimit,
  } = useCreditBalance({live: true});
  const isLegacyGuestAiBlocked = useDesignerSelector(
    selectIsLegacyGuestAiBlocked
  );
  const fileUpload = useFileUpload({siteShortName});
  const showDropzone = useDesignerSelector(selectShowFileUploadDropzone);
  const siteId = useDesignerSelector(selectSiteId);
  const workspaceId = useDesignerSelector(selectWorkspaceId);
  const userId = useDesignerSelector(selectUserId);
  const lastPickedSuggestionPromptRef = React.useRef<string | null>(null);
  const [hoveredPrompt, setHoveredPrompt] = React.useState<string | null>(null);
  const areTemplatesEnabled = useFeatureFlag(
    FEATURE_FLAGS.AICC_COMPONENT_TEMPLATES
  );
  const createCodeComponent = useCodeComponentCreated();
  const componentId = useDesignerSelector(selectComponentCanvasComponentId);
  const creatorTemplateBranding = useCodeComponentSelector(
    selectCreatorTemplateBranding
  );

  // The creator is inherently the AICC creation context (no code component
  // exists yet, so isVcc is false), gate the CMS picker on the flag alone.
  const cmsPickerEnabled = useFeatureFlag(FEATURE_FLAGS.AICC_CMS_PICKER);
  const cmsPicker = useCmsPickerBinding();
  const getCmsItemCount = useCmsCollectionItemCount(cmsPickerEnabled);
  const cmsPickerOnboarding = useCmsPickerOnboardingPopover();

  // Block creation while a pasted secret is present, mirroring the chat composer.
  const {hasSecret, detectSecrets, resetSecrets} = useSecretDetection();
  const secretBlockReason = hasSecret ? SECRET_BLOCK_REASON : undefined;

  const textarea = useTextarea({
    submitBlockedReason: secretBlockReason,
    onSubmit:
      limited || isLegacyGuestAiBlocked
        ? undefined
        : (text: string) => {
            const fromSuggestion =
              lastPickedSuggestionPromptRef.current !== null &&
              text === lastPickedSuggestionPromptRef.current;
            // Reconstitute large-paste chips (EXT-303) into metadata for the
            // creator's first message; the blob is delivered + capped
            // server-side, never inlined into the prompt text.
            const pasteAttachments: PasteAttachment[] =
              textarea.tags.items.flatMap((tag) => {
                const m = tag.metadata;
                return isPasteTagMetadata(m)
                  ? [
                      {
                        id: tag.id,
                        kind: m.pasteType,
                        label: tag.label,
                        content: m.pastedContent,
                        charCount: m.charCount,
                      },
                    ]
                  : [];
              });
            onSubmit(
              text,
              fileUpload.uploadedResults.length > 0
                ? fileUpload.uploadedResults
                : undefined,
              fromSuggestion,
              pasteAttachments.length > 0 ? pasteAttachments : undefined
            );
            fileUpload.clearAll();
            textarea.tags.clear();
            resetSecrets();
          },
    isDisabled: fileUpload.isUploading,
  });

  const isLargePasteEnabled = useFeatureFlag(FEATURE_FLAGS.LARGE_PASTE_CHIP);
  const [editingPasteTagId, setEditingPasteTagId] = React.useState<
    string | null
  >(null);

  // Large paste (EXT-303): collapse an oversized paste into an editable chip.
  // Kept in this host handler (not the shared fileUpload.handlePaste) so the
  // behavior is opt-in per composer.
  const handlePaste = React.useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!isLargePasteEnabled) {
        fileUpload.handlePaste(e);
        return;
      }
      const existingPasteTags = textarea.tags.items.filter((tag) =>
        isPasteTagType(tag.type)
      );
      const decision = evaluateLargePaste(e.clipboardData, {
        count: existingPasteTags.length,
        totalChars: existingPasteTags.reduce(
          (sum, tag) =>
            sum +
            (isPasteTagMetadata(tag.metadata) ? tag.metadata.charCount : 0),
          0
        ),
      });

      if (decision.kind === 'ignore') {
        fileUpload.handlePaste(e);
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (decision.kind === 'reject') {
        toast.danger(largePasteRejectMessage(decision.reason));
        return;
      }

      const {pasteType, text, charCount, lineCount} = decision;
      textarea.tags.add({
        id: generateGuid(),
        type: pasteType === 'code' ? 'paste-code' : 'paste-text',
        label: pasteType === 'code' ? 'Pasted code' : 'Pasted text',
        status: 'success',
        metadata: {
          kind: 'paste',
          pasteType,
          pastedContent: text,
          charCount,
          lineCount,
        },
      });
    },
    [isLargePasteEnabled, fileUpload, textarea.tags]
  );

  React.useEffect(() => {
    detectSecrets(textarea.value);
  }, [textarea.value, detectSecrets]);

  React.useEffect(() => {
    warmCodeComponentStyleProfile(siteShortName);
  }, [siteShortName]);

  const t = useTranslation();

  const cmsTags: TagData[] = cmsPickerEnabled ? cmsPicker.cmsTags : [];
  // Large-paste chips live in textarea.tags (not fileUpload); surface them as
  // render tags so TextareaTags shows them (icon comes from ICON_BY_TYPE).
  const pasteRenderTags: TagData[] = textarea.tags.items
    .filter((tag) => isPasteTagType(tag.type))
    .map((tag) => ({
      id: tag.id,
      type: tag.type,
      label: tag.label,
      status: 'success' as const,
      colorVariant: 'default' as const,
      onDismiss: () => textarea.tags.remove(tag.id),
      onClick: () => setEditingPasteTagId(tag.id),
    }));
  const mergedTags = [...fileUpload.tags, ...cmsTags, ...pasteRenderTags];
  const tags = mergedTags.length > 0 ? mergedTags : undefined;
  const isTextareaEmpty = textarea.value.length === 0;
  const textareaPlaceholder =
    isTextareaEmpty && hoveredPrompt ? hoveredPrompt : DEFAULT_PLACEHOLDER;

  return (
    <ViewportLayer>
      <View
        background="$background1"
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        userSelect="none"
        data-automation-id="code-component-creator"
      >
        <VStack
          width={640}
          align="center"
          position="relative"
          paddingTop={64}
          spacing={8}
        >
          <ComponentsCode64Icon size={64} />
          <VStack spacing={12}>
            <Text
              font="brandedDisplay3"
              colorVariant="primary"
              textAlign="center"
            >
              Generate a code component with AI
            </Text>
            <VStack spacing={4}>
              <FormGroup
                display="flex"
                style={
                  limited || isLegacyGuestAiBlocked ? DISABLED_STYLE : undefined
                }
              >
                <Textarea
                  tags={tags}
                  contextValue={textarea.contextValue}
                  minHeight={140}
                  showDropzone={showDropzone}
                  addFiles={fileUpload.addFiles}
                >
                  <TextareaContainer>
                    <TextareaTags />
                    <TextareaInput
                      placeholder={textareaPlaceholder}
                      hasTag={Boolean(tags?.length)}
                      onPaste={handlePaste}
                    />
                  </TextareaContainer>
                  <TextareaActions>
                    {cmsPickerEnabled ? (
                      // Single "+" entry point. The category menu
                      // offers "CMS Collection" (attach schema) and "Image"
                      // (the existing file picker). Opens downward: the
                      // creator composer sits high on screen with space below.
                      <CmsPickerOnboardingPopover
                        shouldShowPopover={
                          cmsPickerOnboarding.shouldShowPopover
                        }
                        dismiss={cmsPickerOnboarding.dismiss}
                      >
                        <AddContentPopover
                          selectionMode="single"
                          categories={['cms']}
                          getItemCount={getCmsItemCount}
                          actions={[
                            {
                              id: 'image',
                              label: 'Image',
                              icon: ImageIcon,
                              onSelect: fileUpload.openFilePicker,
                            },
                          ]}
                          placement="bottom-start"
                          isDisabled={fileUpload.isUploading}
                          selectedValues={cmsPicker.selectedValues}
                          onChange={cmsPicker.onChange}
                          analytics={{
                            // Opening the picker satisfies the FTU.
                            onOpened: cmsPickerOnboarding.dismiss,
                          }}
                        />
                      </CmsPickerOnboardingPopover>
                    ) : (
                      <IconButton
                        label="Add image"
                        Icon={AddIcon}
                        onClick={fileUpload.openFilePicker}
                        variant="ghost"
                        density="comfortable"
                        isDisabled={fileUpload.isUploading}
                      />
                    )}
                    {isAiCreditsEnabled && (
                      <>
                        <Spacer />
                        <CreditUsagePopover
                          data={creditBalance}
                          isLimited={limited}
                          canExceedLimit={canExceedLimit}
                          isSoftLimited={softLimit}
                          analyticsSource="code_components"
                        />
                      </>
                    )}
                  </TextareaActions>
                </Textarea>
              </FormGroup>
              {hasSecret && <SecretDetectedNote />}
              {isLegacyGuestAiBlocked ? (
                <LegacyGuestAiBlock />
              ) : limited && creditBalance ? (
                <CreditLimitReached
                  usagePeriodEnd={creditBalance.usagePeriodEnd ?? ''}
                  analyticsSource="code_components"
                />
              ) : areTemplatesEnabled ? (
                <CodeComponentCreatorTemplates
                  onSelect={(template) => {
                    trackEvent({
                      name: ANALYTICS_EVENTS.VCC_SUGGESTION_USED,
                      data: {
                        'suggestion label': template.name,
                        siteId,
                        workspaceId: workspaceId ?? undefined,
                        userId,
                      },
                    });
                    createCodeComponent({
                      // Fill this blank component in place: swapping in a new
                      // one deletes its sandbox file, which can land mid-build
                      // and fail tsc with TS6053.
                      componentId: componentId ?? undefined,
                      templateId: template.id,
                      templateBranding: creatorTemplateBranding ?? undefined,
                    });
                  }}
                />
              ) : (
                <HStack spacing={3} justify="center">
                  {EXAMPLES.map((example) => {
                    const handlePreviewStart = () => {
                      if (!isTextareaEmpty) {
                        return;
                      }
                      setHoveredPrompt(example.prompt);
                    };
                    const handlePreviewEnd = () => {
                      setHoveredPrompt(null);
                    };

                    return (
                      <ExampleCard
                        key={example.title}
                        example={example}
                        onMouseEnter={handlePreviewStart}
                        onMouseLeave={handlePreviewEnd}
                        onFocus={handlePreviewStart}
                        onBlur={handlePreviewEnd}
                        onClick={() => {
                          textarea.setValue(example.prompt);
                          setHoveredPrompt(null);
                          lastPickedSuggestionPromptRef.current =
                            example.prompt;
                          trackEvent({
                            name: ANALYTICS_EVENTS.VCC_SUGGESTION_USED,
                            data: {
                              'suggestion label': example.title,
                              siteId,
                              workspaceId: workspaceId ?? undefined,
                              userId,
                            },
                          });
                        }}
                      />
                    );
                  })}
                </HStack>
              )}
              {showUpgradeBanner && upgradeUrl && (
                <Note
                  colorVariant="upgrade"
                  isTinted
                  data-automation-id="vcc-upgrade-banner-creator"
                >
                  <NoteBody>
                    {upgradeBody ??
                      t(
                        'designer.components.code-component.upgrade-banner.body'
                      )}
                  </NoteBody>
                  <NoteActions>
                    <NoteButton
                      href={upgradeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={
                        upgradeCtaLabel
                          ? t(
                              'designer.enterprise-billing.external-action-label',
                              {
                                label: upgradeCtaLabel,
                              }
                            )
                          : undefined
                      }
                      onClick={() => {
                        trackEvent({
                          name: ANALYTICS_EVENTS.VCC_UPGRADE_CTA_CLICKED,
                          data: {
                            surface: 'code_component_creator',
                            siteId,
                            workspaceId: workspaceId ?? undefined,
                            userId,
                          },
                        });
                      }}
                    >
                      {upgradeCtaLabel ??
                        t(
                          'designer.components.code-component.upgrade-banner.upgrade-plan-action'
                        )}
                    </NoteButton>
                  </NoteActions>
                </Note>
              )}
            </VStack>
          </VStack>
        </VStack>
      </View>

      <HiddenFileInput
        fileInputRef={fileUpload.fileInputRef}
        onChange={fileUpload.handleFileInputChange}
      />

      {editingPasteTagId &&
        (() => {
          const tag = textarea.tags.get(editingPasteTagId);
          const m = tag?.metadata;
          if (!tag || !isPasteTagMetadata(m)) return null;
          return (
            <EditPasteModal
              // Key by tag id so switching chips remounts with fresh content.
              key={editingPasteTagId}
              pasteType={m.pasteType}
              initialContent={m.pastedContent}
              onClose={() => setEditingPasteTagId(null)}
              onSave={(content) =>
                textarea.tags.update(editingPasteTagId, (prev) => ({
                  metadata: {
                    ...(isPasteTagMetadata(prev.metadata) ? prev.metadata : m),
                    pastedContent: content,
                    charCount: content.length,
                    lineCount: countLines(content),
                  },
                }))
              }
            />
          );
        })()}
    </ViewportLayer>
  );
};
